<div class="container mx-auto p-4">
  <h1 class="text-2xl font-bold mb-4">{{ 'PIGLET.APPLICATION_SETTINGS.TITLE' | translate }}</h1>

  <!-- Loading indicator -->
  <div *ngIf="loading">
    <app-loading />
  </div>

  <!-- Error message -->
  <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
    <span>{{ error }}</span>
  </div>

  <!-- Success message -->
  <div *ngIf="success" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
    <span>{{ success }}</span>
  </div>

  <!-- Action buttons -->
  <div *ngIf="!loading" class="flex justify-end mb-4 space-x-3">
    <button
      (click)="discardChanges()"
      class="btn bg-gray-300 hover:bg-gray-400 border-gray-300 hover:border-gray-400 text-gray-800 rounded-md"
      [disabled]="changedSettings.length === 0"
      [ngClass]="{'opacity-50 cursor-not-allowed': changedSettings.length === 0}">
      {{ 'PIGLET.APPLICATION_SETTINGS.DISCARD_CHANGES' | translate }}
    </button>
    <button
      (click)="openConfirmModal()"
      class="btn bg-blue-500 hover:bg-blue-700 border-blue-500 hover:border-blue-700 text-white rounded-md"
      [disabled]="changedSettings.length === 0"
      [ngClass]="{'opacity-50 cursor-not-allowed': changedSettings.length === 0}">
      {{ 'PIGLET.APPLICATION_SETTINGS.SAVE_CHANGES' | translate }} ({{ changedSettings.length }})
    </button>
  </div>

  <!-- Settings list -->
  <div *ngIf="!loading" class="shadow-md rounded-lg overflow-hidden">
    <table class="table-auto w-full border-collapse border border-slate-400">
      <thead>
        <tr class="bg-slate-100 dark:bg-slate-700">
          <th class="border border-slate-300 dark:border-slate-600 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.KEY' | translate }}</th>
          <th class="border border-slate-300 dark:border-slate-600 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.VALUE' | translate }}</th>
          <th class="border border-slate-300 dark:border-slate-600 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.SENSITIVE' | translate }}</th>
          <th class="border border-slate-300 dark:border-slate-600 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.ACTIONS' | translate }}</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <tr *ngFor="let setting of settings; let even = even"
            [ngClass]="isSettingChanged(setting.key) ? 'bg-blue-50 dark:bg-blue-900/20' : (even ? 'bg-slate-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-900')">
          <td class="border border-slate-300 dark:border-slate-600 px-6 py-4 whitespace-nowrap">
            <p>{{ setting.key }}</p>
            <span class="text-xs text-gray-500">
              {{ `PIGLET.APPLICATION_SETTINGS.DESCRIPTIONS.${setting.key}` | translate }}
            </span>
          </td>
          <td class="border border-slate-300 dark:border-slate-600 px-6 py-4 whitespace-nowrap">
            <!-- Non-sensitive value or not editing -->
            <span *ngIf="!setting.isSensitiveData && !editingKeys.has(setting.key)">{{ setting.value }}</span>

            <!-- Sensitive value and not editing -->
            <span *ngIf="setting.isSensitiveData && !editingKeys.has(setting.key)">********</span>

            <!-- Editing mode -->
            <input *ngIf="editingKeys.has(setting.key)"
                   type="text"
                   [(ngModel)]="setting.value"
                   (ngModelChange)="onSettingChange(setting)"
                   class="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
          </td>
          <td class="border border-slate-300 dark:border-slate-600 px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <input type="checkbox"
                     [(ngModel)]="setting.isSensitiveData"
                     (ngModelChange)="onSettingChange(setting)"
                     class="form-checkbox h-4 w-4 text-blue-600 mr-2">
              <span *ngIf="setting.isSensitiveData" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Yes</span>
              <span *ngIf="!setting.isSensitiveData" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">No</span>
            </div>
          </td>
          <td class="border border-slate-300 dark:border-slate-600 px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
            <button (click)="startEditing(setting)" class="text-indigo-600 hover:text-indigo-900">
              <i class="uil uil-edit h-8 w-8 fill-violet-600/5"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Confirmation Modal -->
<div [ngClass]="showConfirmModal ? '' : 'hidden'" class="fixed z-50 overflow-hidden inset-0 m-auto flex items-center justify-center w-full h-[100vh]" style="background-color: #000000a1;">
  <div class="relative w-full h-auto max-w-2xl p-4">
    <div class="relative bg-white dark:bg-slate-900 rounded-lg shadow dark:shadow-gray-800">
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <h5 class="text-xl font-semibold">{{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.TITLE' | translate }}</h5>
        <button type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ms-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white" (click)="closeConfirmModal()">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
        </button>
      </div>
      <div class="p-6">
        <p class="mb-4">{{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.DESCRIPTION' | translate }}</p>

        <div class="max-h-60 overflow-y-auto mb-4">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.KEY' | translate }}</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.ORIGINAL_VALUE' | translate }}</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.NEW_VALUE' | translate }}</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'PIGLET.APPLICATION_SETTINGS.SENSITIVE' | translate }}</th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr *ngFor="let setting of changedSettings">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">{{ setting.key }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <ng-container *ngFor="let orig of originalSettings">
                    <span *ngIf="orig.key === setting.key">{{ orig.value }}</span>
                  </ng-container>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span>{{ setting.value }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span *ngIf="setting.isSensitiveData" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Yes</span>
                  <span *ngIf="!setting.isSensitiveData" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">No</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end space-x-3">
          <button type="button" class="btn bg-gray-300 hover:bg-gray-400 border-gray-300 hover:border-gray-400 text-gray-800 rounded-md" (click)="closeConfirmModal()">
            {{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.CANCEL' | translate }}
          </button>
          <button type="button"
                  class="btn bg-blue-500 hover:bg-blue-700 border-blue-500 hover:border-blue-700 text-white rounded-md flex items-center justify-center"
                  [disabled]="loading"
                  [ngClass]="{'opacity-50 cursor-not-allowed': loading}"
                  (click)="saveChanges()">
            <span *ngIf="loading" class="inline-block mr-2">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            {{ 'PIGLET.APPLICATION_SETTINGS.CONFIRM_MODAL.CONFIRM' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
